



























































import {
  computed, defineComponent, reactive, ref,
} from '@vue/composition-api';
import { useFormat } from 'vivat';
import { useQuery, useResult } from '@vue/apollo-composable';

import useHighChartMap from '@/useHighChartMap';
import { colorsFn } from '@/useApexchartsColor';

import getGP from '@/gql/report/analytic/getGP.gql';

import Heatmap from '@/components/Map/Heatmap.vue';
import CommonIconTop from '@/components/CommonIconTop.vue';

const NOW = new Date();

const Income = defineComponent({
  components: {
    Heatmap,
    CommonIconTop,
  },
  setup() {
    const { getProperty } = useHighChartMap();
    const toDate = (date: string) => useFormat.date(date).displayFullThaiDate();
    const toCurrency = (string: string) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const getYear = (date: string) => useFormat.date(date).dayjs.get('year');
    const backwardTenYear = Array.from({ length: 10 }, (x, i) => new Date().getFullYear() + 543 - i);
    const filter = reactive({
      range: {
        from: useFormat.date(NOW).computerShortDate(),
        to: useFormat.date(NOW).computerShortDate(),
      },
    });
    const computedFilter = computed(() => {
      if (filter.range.from === filter.range.to) return {};
      return {
        range: {
          from: useFormat.date(filter.range.from).computerShortDate(),
          to: useFormat.date(filter.range.to).computerShortDate(),
        },
      };
    });
    const donutType = [
      { display: 'By Top Hub/Branch (%GP)', name: 'topHub' },
      { display: 'By Top Area (%GP)', name: 'topArea' },
      { display: 'By Top Province (%GP)', name: 'topProvince' },
      { display: 'By Bottom Hub/Branch (%GP)', name: 'bottomHub' },
      { display: 'By Bottom Area (%GP)', name: 'bottomArea' },
      { display: 'By Bottom Province (%GP)', name: 'bottomProvince' },
    ];

    const { result: getGPResult } = useQuery(getGP, () => ({
      timeframe: computedFilter.value.range,
    }));
    const gpSummary = useResult(getGPResult, [], (data) => data.gpSummary);
    const netProfits = useResult(getGPResult, [], (data) => data.netProfit);
    const provinces = useResult(getGPResult, [], (data) => data.provinces);
    const topHubs = useResult(getGPResult, [], (data) => data.topHubs);
    const bottomHubs = useResult(getGPResult, [], (data) => data.bottomHubs);
    const topAreas = useResult(getGPResult, [], (data) => data.topAreas);
    const bottomAreas = useResult(getGPResult, [], (data) => data.bottomAreas);
    const topProvinces = useResult(getGPResult, [], (data) => data.topProvinces);
    const bottomProvinces = useResult(getGPResult, [], (data) => data.bottomProvinces);

    const thMonthsMini = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
    const selectedYear = ref(new Date().getFullYear() + 543);
    const computedSeriesData = computed(() => {
      const result = { gp: [], net: [] };
      result.gp = gpSummary.value.map((val: any) => ({
        x: thMonthsMini[val.month - 1],
        y: toCurrency(val.gp),
      }));
      result.net = netProfits.value.map((val: any) => ({
        x: thMonthsMini[val.month - 1],
        y: toCurrency(val.netProfit),
      }));

      return result;
    });

    const areaCharts = computed(() => ({
      options: {
        dataLabels: { enabled: false },
        yaxis: {
          opposite: false,
          labels: {
            formatter: (val: any) => toCurrency(val),
          },
        },
        // colors: colorsFn,
        stroke: {
          show: true,
          curve: 'smooth',
          lineCap: 'butt',
          colors: undefined,
          width: 7,
          dashArray: 0,
        },
      },
      series: [
        {
          type: 'area',
          name: 'กำไร (บาท)',
          data: computedSeriesData.value.gp,
        },
        {
          type: 'area',
          name: 'กำไรสุทธิ (บาท)',
          data: computedSeriesData.value.net,
        },
      ],
    }));

    const colorIndex = ref(0);
    const COLORS = ['pink', 'green', 'blue', 'yellow', 'red', 'gray', 'orange', 'teal', 'indigo', 'purple'];
    function getColor() {
      if (colorIndex.value === 9) colorIndex.value = 0;
      else colorIndex.value += 1;
      return COLORS[colorIndex.value];
    }

    const donutChart = computed(() => {
      let topHub: any = [];
      if (topHubs.value.length === 0) topHub = [];
      else {
        topHub = topHubs.value?.map((areaElm: any) => ({
          name: areaElm.name,
          data: areaElm.gp,
          color: getColor(),
        }));
      }

      let topArea: any = [];
      if (topAreas.value.length === 0) topHub = [];
      else {
        topArea = topAreas.value?.map((areaElm: any) => ({
          name: areaElm.name,
          data: areaElm.gp,
          color: getColor(),
        }));
      }

      let topProvince: any = [];
      if (topProvinces.value.length === 0) topHub = [];
      else {
        topProvince = topProvinces.value?.map((areaElm: any) => ({
          name: areaElm.name,
          data: areaElm.gp,
          color: getColor(),
        }));
      }

      let bottomHub: any = [];
      if (topHubs.value.length === 0) topHub = [];
      else {
        bottomHub = bottomHubs.value?.map((areaElm: any) => ({
          name: areaElm.name,
          data: areaElm.gp,
          color: getColor(),
        }));
      }

      let bottomArea: any = [];
      if (bottomAreas.value.length === 0) topHub = [];
      else {
        bottomArea = bottomAreas.value?.map((areaElm: any) => ({
          name: areaElm.name,
          data: areaElm.gp,
          color: getColor(),
        }));
      }

      let bottomProvince: any = [];
      if (bottomProvinces.value.length === 0) topHub = [];
      else {
        bottomProvince = bottomProvinces.value?.map((areaElm: any) => ({
          name: areaElm.name,
          data: areaElm.gp,
          color: getColor(),
        }));
      }

      return {
        topHub,
        bottomHub,
        topArea,
        bottomArea,
        topProvince,
        bottomProvince,
      };
    });

    const computedData = computed(() => {
      const _value: any = {
        northern: [],
        northeastern: [],
        central: [],
        eastern: [],
        western: [],
        southern: [],
      };

      function pushToArray(_region: string, province: string | number, value: number | string) {
        if (_region === 'NORTHERN') _value.northern.push([`th-${province}`, value]);
        else if (_region === 'NORTHEASTERN') _value.northeastern.push([`th-${province}`, value]);
        else if (_region === 'CENTRAL') _value.central.push([`th-${province}`, value]);
        else if (_region === 'EASTERN') _value.eastern.push([`th-${province}`, value]);
        else if (_region === 'WESTERN') _value.western.push([`th-${province}`, value]);
        else if (_region === 'SOUTHERN') _value.southern.push([`th-${province}`, value]);
      }

      const hasValue: number[] = [];
      // eslint-disable-next-line
      provinces.value?.forEach((elm: any) => {
        hasValue.push(elm.provinceId);
        const data = elm.gp;
        const { shortForm, region } = getProperty(elm.provinceId);
        pushToArray(region, shortForm, data > 0 ? toCurrency(data) : 0);
      });
      for (let i = 0; i < 78; i += 1) {
        if (hasValue.indexOf(i) === -1) {
          const { shortForm, region } = getProperty(i);
          pushToArray(region, shortForm, 0);
        }
      }
      return _value;
    });

    return {
      toCurrency,
      toDate,
      getYear,
      filter,

      donutType,
      areaCharts,
      donutChart,
      backwardTenYear,
      selectedYear,

      computedData,
    };
  },
});

export default Income;
